import { defaultProjSettings } from 'pages/ProjectsPage/projectUtils'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'
import { indexedDBStorage } from './persistDb'
import { Workspace2Store } from './types/types'

/**
 * Creates a zustand store for managing workspace state
 * @returns {Workspace2Store} A reactive store with workspace management methods
 */
export const useWorkspace2Store = create<Workspace2Store>()(
    persist(
        (set) => ({
            /** initial state */
            isContentTeamFromStudioPage: false,
            treeData: [],
            selectedProjectElementNode: {},
            selectedProjectElementNodeId: '',
            activeTab: 1,
            articlesLoading: [],
            expandedKeys: [],
            contentItemViewNodes: [],
            isTrackChangesExpanded: false,
            previousContentItemViewNodes: [],
            classificationNodes: [],
            textContentElementToAdd: null,
            isTreeLoading: false,
            isEditingPlainText: false,
            projectSettings: defaultProjSettings,
            editorDeactivationSignal: 0,

            /** actions/setters */
            actions: {
                setIsContentTeamFromStudioPage: (isContentTeamFromStudioPage) =>
                    set({ isContentTeamFromStudioPage }),
                setTreeData: (treeData) => set({ treeData }),
                setSelectedProjectElementNode: (selectedProjectElementNode) => {
                    set({ selectedProjectElementNode })

                    set({
                        selectedProjectElementNodeId:
                            selectedProjectElementNode?.id ||
                            selectedProjectElementNode?.parentNodeId ||
                            '',
                    })
                },
                setActiveTab: (activeTab) => set({ activeTab }),
                setArticlesLoading: (articlesLoading) =>
                    set({ articlesLoading }),
                setExpandedKeys: (keys) => set({ expandedKeys: keys }),
                setContentItemViewNodes: (contentItemViewNodes) =>
                    set((state) => ({
                        contentItemViewNodes,
                        editorDeactivationSignal:
                            state.editorDeactivationSignal + 1,
                    })),
                setPreviousContentItemViewNodes: (
                    previousContentItemViewNodes
                ) => set({ previousContentItemViewNodes }),
                setClassificationNodes: (classificationNodes) =>
                    set({ classificationNodes }),
                setTextContentElementToAdd: (textContentElementToAdd) =>
                    set({ textContentElementToAdd }),
                switchWorkspace: (enabled) =>
                    set({ isBetaWorkspaceEnabled: enabled }),
                setMaintenanceMode: (error) =>
                    set({
                        isUnderMaintenance: error !== null,
                        lastError: error,
                    }),
                setDefault: () =>
                    set({
                        articlesLoading: ['default'],
                        contentItemViewNodes: [],
                        previousContentItemViewNodes: [],
                        textContentElementToAdd: null,
                    }),
                setIsTrackChangesExpanded: (isTrackChangesExpanded) => {
                    set({ isTrackChangesExpanded })
                },
                setIsTreeLoading: (isTreeLoading: boolean) =>
                    set({ isTreeLoading }),
                setProjectSettings: (projectSettings) =>
                    set({ projectSettings }),
                setIsEditingPlainText: (isEditingPlainText) =>
                    set({ isEditingPlainText }),
            },
        }),
        {
            name: 'workspace2-store',
            // versioning for future migrations
            version: 1.1,
            storage: indexedDBStorage,
            // partial persistence: explicitly choose which state properties to persist
            partialize: (state) => ({
                contentItemViewNodes: state.contentItemViewNodes,
                classificationNodes: state.classificationNodes,
                expandedKeys: state.expandedKeys,
                selectedProjectElementNodeId:
                    state.selectedProjectElementNodeId,
                activeTab: state.activeTab,
            }),
        }
    )
)

/**
 * A custom hook that retrieves a shallowly selected value from the `useWorkspace2Store` store.
 *
 * Sample usage:
 * ```typescript
 * const { activeTab, isTreeLoading } = useShallowSelector((state) => ({
 *   activeTab: state.activeTab,
 *   isTreeLoading: state.isTreeLoading,
 * }));
 * ```
 *
 * @param selector - A function that takes the store state and returns the value to be selected.
 * @returns The selected value from the store.
 */
export const useShallowSelector = <T>(
    selector: (state: Workspace2Store) => T
): T => useWorkspace2Store(useShallow(selector))

/**
 * Zustand selector that determines if the current workspace is from OM/SMM Page.
 * @returns The content team from studio page status from store.
 */
export const useIsContentTeamFromStudioPage = () =>
    useWorkspace2Store((state) => state.isContentTeamFromStudioPage)
/**
 * Zustand selector that gets the beta workspace status.
 * @returns The beta workspace status from store.
 */
export const useIsBetaWorkspaceEnabled = () =>
    useWorkspace2Store((state) => state.isBetaWorkspaceEnabled)
/**
 * Zustand selector that gets the maintenance mode status.
 * @returns The maintenance mode status from store.
 */
export const useIsUnderMaintenance = () =>
    useWorkspace2Store((state) => state.isUnderMaintenance)
/**
 * Zustand selector that gets the last error.
 * @returns The last error from store.
 */
export const useLastError = () => useWorkspace2Store((state) => state.lastError)
/**
 * Zustand selector that gets the selected project element node.
 * @returns The selected project element node from store.
 */
export const useSelectedProjectElementNode = () =>
    useWorkspace2Store((state) => state.selectedProjectElementNode)
/**
 * Zustand selector that gets the selected project element node ID.
 * @returns The selected project element node ID from store.
 */
export const useSelectedProjectElementNodeId = () =>
    useWorkspace2Store((state) => state.selectedProjectElementNodeId)
/**
 * Zustand selector that gets the active tab.
 * @returns The active tab from store.
 */
export const useActiveTab = () => useWorkspace2Store((state) => state.activeTab)
/**
 * Zustand selector that gets the articles loading state.
 * @returns The articles loading state from store.
 */
export const useArticlesLoading = () =>
    useWorkspace2Store((state) => state.articlesLoading)
/**
 * Zustand selector that gets the expanded keys.
 * @returns The expanded keys from store.
 */
export const useExpandedKeys = () =>
    useWorkspace2Store((state) => state.expandedKeys)
/**
 * Zustand selector that gets the content item view nodes.
 * @returns The content item view nodes from store.
 */
export const useContentItemViewNodes = () =>
    useWorkspace2Store((state) => state.contentItemViewNodes)
/**
 * Zustand selector that gets the text content element currently being added.
 * @returns The text content element currently being added from store.
 */
export const useTextContentElementToAdd = () =>
    useWorkspace2Store((state) => state.textContentElementToAdd)

/**
 * A custom hook that retrieves the `classificationNodes` state from the `useWorkspace2Store` store.
 *
 * @returns The `classificationNodes` state from the workspace store.
 */
export const useClassificationNodes = () =>
    useWorkspace2Store((state) => state.classificationNodes)
/**
 * Zustand selector that gets the tree loading state.
 * @returns The tree loading state from store.
 */
export const useIsTreeLoading = () =>
    useWorkspace2Store((state) => state.isTreeLoading)
/**
 * Zustand selector that gets the current project settings.
 * @returns The project settings from store.
 */
export const useProjectSettings = () =>
    useWorkspace2Store((state) => state.projectSettings)
/** Zustand selector that gets the actions/setters.
 *
 * Actions/setters are stable and won't change between re-renders,
 * so we can destructure the specific actions needed.
 *
 * @example
 * ```typescript
 *     const { setSelectedProjectElementNode, ...etc } = useWorkspace2Actions()
 * ```
 */
export const useWorkspace2Actions = () =>
    useWorkspace2Store((state) => state.actions)
